/* eslint-disable max-lines */
/* eslint-disable react/jsx-no-useless-fragment */
import { SkeletonMotion, ThemeTokens } from '@acp/core-ui';
import { formatPrice, Invoice, PropertyInvoice } from '@acp/shared';
import {
    Badge,
    BorderProps,
    Box,
    Button,
    Card,
    CardBody,
    CardBodyProps,
    Flex,
    Grid,
    GridItem,
    GridItemProps,
    HStack,
    Image,
    Link,
    Skeleton,
    Text,
    Tooltip,
    VStack,
} from '@chakra-ui/react';
import TimeAgo from 'react-timeago';
import { format } from 'date-fns';
import { brandIcons } from '@acp/core-ui/assets';
import { Link as RouterLink } from 'react-router-dom';

const COLORED_BORDER_WIDTH = 6;
const CARD_BORDER_PADDING = 5;
const gap = 4;

// define the grid areas for the invoice row layout
const gridAreaProps: { [key: string]: GridItemProps } = {
    'invoice-number': {
        gridArea: 'invoice-number',
        as: HStack,
        gap: 2,
        mb: [gap, null, gap, 0],
    },
    // services is for property invoice row
    services: {
        gridArea: 'services',
        mb: [gap, 0, 0, 0],
    },
    property: {
        gridArea: 'property',
        mb: [gap, 0, 0, 0],
    },
    'due-date': {
        gridArea: 'due-date',
        as: VStack,
        alignItems: 'start',
        justifySelf: ['start', 'end', null, 'start'],
        gap: 1,
        pl: [0, 2, null, 0],
        mb: [2, 2, 2, 0],
    },
    total: {
        gridArea: 'total',
        justifySelf: ['start', 'end', null, 'start'],
        mb: [2, 0, 0, 0],
    },
    actions: {
        gridArea: 'actions',
        as: HStack,
        justifyContent: ['start', 'end', null, 'end'],
        width: '100%',
        gap: 3,
        alignSelf: ['end', null, null, 'center'],
    },
};

const gridTemplateAreasProps = [`
    "invoice-number invoice-number"
    "property property"
    "due-date ."
    "total ."
    "actions ."
`, `
    "invoice-number invoice-number due-date"
    "invoice-number invoice-number total"
    "property property actions"
`, null, '"invoice-number property due-date total actions"'];

const gridTemplateColumnsProps = ['1fr 1fr', 'min-content 1fr 1fr', null, '3fr 5fr 3fr 2fr 4fr'];

type AreaType = keyof typeof gridAreaProps;

interface InvoiceRowColumnProps extends GridItemProps {
    area: AreaType;
}

function InvoiceRowSkeleton() {
    return (
        <Card
            borderWidth={1}
            borderColor={ThemeTokens.Defaults.borderColor}
            bg="white"
            variant="filled"
            w="full"
            p={5}
        >
            <Grid
                gridTemplateAreas={gridTemplateAreasProps}
                gridTemplateColumns={gridTemplateColumnsProps}
                gridTemplateRows="auto"
                gap={0}
                alignItems="center"
                justifyItems="start"
                h="auto"
            >
                <GridItem as={VStack} gap={1} gridArea="invoice-number" mb={[gap, null, null, 0]} alignItems="start">
                    <Skeleton>
                        <Text fontSize="sm">Invoice extra</Text>
                    </Skeleton>
                    <Skeleton>
                        <Text fontSize="x-small">
                            #12345678
                        </Text>
                    </Skeleton>
                </GridItem>
                <VStack
                    alignItems="start"
                    gap={1}
                    w={52}
                    gridArea="property"
                    mb={[gap, null, null, 0]}
                >
                    <SkeletonMotion height={5} />
                    <SkeletonMotion height={5} />
                </VStack>

                <GridItem as={VStack} gridArea="due-date" alignItems="start" justifySelf="start" gap={1} pl={[0, 2, null, 0]} mb={[gap, 0]}>
                    <Skeleton>
                        <Text fontSize="xs">
                            Due in 3 days
                        </Text>
                    </Skeleton>
                </GridItem>

                <GridItem as={Flex} justifyContent={['start', null, null, 'end']} gridArea="total" justifySelf={['start', null, null, 'end']} w={24}>
                    <SkeletonMotion height={5} />
                </GridItem>

                <GridItem as={HStack} gridArea="actions" justifyContent="end" width="100%" gap={3} alignSelf={['end', null, null, 'center']}>
                    <Skeleton>
                        <Button
                            borderRightWidth={1}
                            borderRightColor="secondary.900"
                            color="secondary.25"
                            size="sm"
                        >
                            Action menu xx
                        </Button>
                    </Skeleton>
                </GridItem>
            </Grid>
        </Card>
    );
}

function InvoiceRowHeader({ children }: { children: React.ReactNode }) {
    return (
        <Box
            borderBottomWidth={1}
            px={CARD_BORDER_PADDING}
            pb={2}
            fontSize={{ base: 12, lg: 13 }}
            w="full"
            borderLeftWidth={COLORED_BORDER_WIDTH}
            borderLeftColor="transparent"
        >
            {children}
        </Box>
    );
}

function InvoiceRowBody({ children, as: Component, ...props }: CardBodyProps) {
    return (
        <CardBody as={Component} {...props}>
            {children}
        </CardBody>
    );
}

function InvoiceRowColumn({ children, area, ...props }: InvoiceRowColumnProps) {
    const gridItemAreaProps = gridAreaProps[area];

    return (
        <GridItem {...gridItemAreaProps} {...props}>
            {children}
        </GridItem>
    );
}

function InvoiceStatus({ invoice }: { invoice: Invoice | PropertyInvoice }) {
    if (invoice.isPaid) {
        return (
            <Tooltip label={`Paid at ${format(new Date(invoice.paidAt as string), 'dd/MM/yyyy')}`} bgColor="white" color="black">
                <Badge colorScheme="green" variant="solid" userSelect="none">
                    Paid
                    {' '}
                    <TimeAgo
                        date={invoice.paidAt as string}
                        title=""
                        live={false}
                    />
                </Badge>
            </Tooltip>
        );
    }

    return (
        <Tooltip label={`Due ${format(invoice.dueDate, 'dd/MM/yyyy')}`} bgColor="white" color="black">
            <Badge colorScheme={invoice.isOverdue ? 'danger' : 'info'} variant="subtle" userSelect="none">
                Due
                {' '}
                <TimeAgo
                    date={invoice.dueDate}
                    title=""
                    live={false}
                />
            </Badge>
        </Tooltip>
    );
}

function InvoiceAmount({ amountIncGst }: { amountIncGst: string }) {
    return (
        <HStack alignItems="baseline" gap={1}>
            <Text>
                $
                {formatPrice(parseFloat(amountIncGst))}
            </Text>
            <Text fontSize="0.7em" color="muted.500" minW={42}>
                inc. GST
            </Text>
        </HStack>
    );
}

function InvoiceIcon({ isPaid }: { isPaid: boolean }) {
    if (isPaid) {
        return (
            <Image src={brandIcons.invoiceSmMuted} h="27px" />
        );
    }

    return (
        <Image src={brandIcons.overdueInvoiceSmMuted} h="30px" />
    );
}

function InvoiceNumber({ invoiceNumber }: { invoiceNumber: string }) {
    return (
        <VStack gap={0} alignItems="start">
            <Text fontWeight="medium">Invoice</Text>
            <Text fontSize="sm">
                #
                {invoiceNumber}
            </Text>
        </VStack>
    );
}

function InvoiceAddress({ invoice }: { invoice: Invoice | PropertyInvoice }) {
    return (
        <Link
            as={RouterLink}
            to={`/property/${invoice.property.compliancePropertyId}`}
            fontWeight="medium"
            textDecoration="none"
            _hover={{
                textDecoration: 'underline',
            }}
        >
            <VStack alignItems="start" gap={0}>
                <Text>{invoice.property.addressLine1}</Text>
                <Text>{invoice.property.addressLine2}</Text>
            </VStack>
        </Link>
    );
}

export function InvoiceRowLayout(
    {
        children,
        borderLeftColor = 'secondary.500',
    }:
    {
        children: React.ReactNode;
        borderLeftColor?: BorderProps['borderLeftColor'];
    }
) {
    return (
        <Card
            borderColor={ThemeTokens.Defaults.borderColor}
            borderWidth={1}
            borderLeftWidth={6}
            borderLeftColor={borderLeftColor}
            bg="white"
            variant="filled"
            w="full"
            shadow="sm"
            p={5}
        >
            {children}
        </Card>
    );
}

InvoiceRowLayout.Skeleton = InvoiceRowSkeleton;
InvoiceRowLayout.Body = InvoiceRowBody;
InvoiceRowLayout.Column = InvoiceRowColumn;
InvoiceRowLayout.InvoiceStatus = InvoiceStatus;
InvoiceRowLayout.InvoiceAmount = InvoiceAmount;
InvoiceRowLayout.InvoiceIcon = InvoiceIcon;
InvoiceRowLayout.InvoiceNumber = InvoiceNumber;
InvoiceRowLayout.Address = InvoiceAddress;
InvoiceRowLayout.Header = InvoiceRowHeader;
